// frontend/src/components/assessment/artifacts/CaseStudyForm.js
// Step 3 of the assessment UI: case study form with autosave and AI-sourced field highlighting.

import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';

// ---------------------------------------------------------------------------
// Internal CaseStudyField component — NOT exported
// ---------------------------------------------------------------------------

const CaseStudyField = ({ label, description, content = null, source, onChange, indented = false, singleLine = false, readOnly = false }) => {
  const textareaRef = useRef(null);
  const [highlighted, setHighlighted] = useState(false);
  const prevContentRef = useRef(content);

  // Auto-expand height whenever content changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [content]);

  // Highlight pulse when AI updates a non-empty field with new non-empty content
  useEffect(() => {
    if (
      source === 'ai' &&
      content &&
      content.length > 0 &&
      prevContentRef.current &&
      prevContentRef.current.length > 0 &&
      content !== prevContentRef.current
    ) {
      setHighlighted(true);
      const timer = setTimeout(() => setHighlighted(false), 700);
      prevContentRef.current = content;
      return () => clearTimeout(timer);
    }
    prevContentRef.current = content;
  }, [content, source]); 

  const textareaClassName = [
    'cs-textarea',
    singleLine  ? 'single-line'  : '',
    source === 'ai'   ? 'ai-sourced'   : '',
    source === 'user' ? 'user-sourced' : '',
    highlighted ? 'highlighted' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`case-study-field${indented ? ' indented' : ''}`}>
      <div className="cs-field-header">
        <div className="cs-field-label">{label}</div>
        <div className="cs-field-description">{description}</div>
      </div>
      <textarea
        ref={textareaRef}
        className={textareaClassName}
        rows={singleLine ? 1 : 3}
        placeholder="Describe this in your own words…"
        value={content || ''}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

CaseStudyField.propTypes = {
  label:       PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  content:     PropTypes.string,
  source:      PropTypes.oneOf(['none', 'ai', 'user']).isRequired,
  onChange:    PropTypes.func.isRequired,
  indented:    PropTypes.bool,
  singleLine:  PropTypes.bool,
  readOnly:    PropTypes.bool,
};


// ---------------------------------------------------------------------------
// Exported CaseStudyForm component
// ---------------------------------------------------------------------------

const CaseStudyForm = forwardRef(({
  caseStudy,
  userGoals,
  conversationId,
  onFieldChange,
  onGoalsFieldChange,
  onSaveStatusChange,
  readOnly = false,
}, ref) => {
  const { user } = useAuth();

  const caseStudyRef = useRef(caseStudy);
  const userGoalsRef = useRef(userGoals);
  const saveTimerRef = useRef(null);
  const savedDisplayTimerRef = useRef(null);

  // Keep refs in sync with latest props
  useEffect(() => {
    caseStudyRef.current = caseStudy;
  }, [caseStudy]);

  useEffect(() => {
    userGoalsRef.current = userGoals;
  }, [userGoals]);

  const dirtyRef    = useRef(false);
  const inflightRef = useRef(null);

  // Perform the PATCH now. Extracted from the debounce so flush() can force it.
  const doSave = useCallback(() => {
    dirtyRef.current = false;
    onSaveStatusChange('saving');

    const cs = caseStudyRef.current;
    const ug = userGoalsRef.current;

    // Only send user-sourced fields to avoid overwriting AI-populated sources in the DB.
    // The PATCH endpoint always writes source: 'user', so sending AI-sourced content
    // would corrupt the source and block future AI updates after page refresh.
    const payload = {};
    if (cs.characterBrief?.inSum?.source          === 'user') payload.characterBriefInSum          = cs.characterBrief.inSum.content;
    if (cs.realIssue?.summary?.source             === 'user') payload.realIssueSummary             = cs.realIssue.summary.content;
    if (cs.realIssue?.dataAndReasoning?.source    === 'user') payload.realIssueDataAndReasoning    = cs.realIssue.dataAndReasoning.content;
    if (cs.challenge?.source                      === 'user') payload.challenge                    = cs.challenge.content;
    if (cs.characterStakes?.downSides?.inSum?.source === 'user') payload.characterStakesDownSidesInSum = cs.characterStakes.downSides.inSum.content;
    if (cs.characterStakes?.upSides?.inSum?.source   === 'user') payload.characterStakesUpSidesInSum   = cs.characterStakes.upSides.inSum.content;
    if (cs.desiredOutcome?.source                 === 'user') payload.desiredOutcome                = cs.desiredOutcome.content;

    const goalsPayload = {};
    if (ug?.learningGoal?.source === 'user') goalsPayload.learningGoal = ug.learningGoal.content;

    if (Object.keys(payload).length === 0 && Object.keys(goalsPayload).length === 0) {
      onSaveStatusChange(null);
      return Promise.resolve();
    }

    const requests = [];
    if (Object.keys(payload).length > 0) {
      requests.push(apiService.patch(`/api/v1/conversations/${conversationId}/case-study`, payload, user));
    }
    if (Object.keys(goalsPayload).length > 0) {
      requests.push(apiService.patch(`/api/v1/conversations/${conversationId}/user-goals`, goalsPayload, user));
    }
    const p = Promise.all(requests)
      .then(() => {
        onSaveStatusChange('saved');
        if (savedDisplayTimerRef.current) clearTimeout(savedDisplayTimerRef.current);
        savedDisplayTimerRef.current = setTimeout(() => onSaveStatusChange(null), 2000);
      })
      .catch((err) => {
        console.error('[CaseStudyForm] Autosave failed:', err);
        onSaveStatusChange(null);
      });
    inflightRef.current = p;
    return p;
  }, [conversationId, user, onSaveStatusChange]);

  const triggerAutosave = useCallback(() => {
    dirtyRef.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(doSave, 1000);
  }, [doSave]);

  // Force any pending/in-flight save to settle before an AI turn (called via ref).
  const flush = useCallback(async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (dirtyRef.current) return doSave();
    if (inflightRef.current) return inflightRef.current;
    return undefined;
  }, [doSave]);

  useImperativeHandle(ref, () => ({ flush }), [flush]);

  const handleFieldChange = useCallback((field, value) => {
    onFieldChange(field, value);
    triggerAutosave();
  }, [onFieldChange, triggerAutosave]);

  const handleGoalsFieldChange = useCallback((field, value) => {
    onGoalsFieldChange(field, value);
    triggerAutosave();
  }, [onGoalsFieldChange, triggerAutosave]);

  const learningGoal = userGoals?.learningGoal;
  const showLearningGoal = learningGoal && (learningGoal.content || learningGoal.source === 'user');

  return (
    <div className="case-study-form">
      <CaseStudyField
        label="Counterpart"
        description="Who you're talking to and their role"
        singleLine
        content={caseStudy.characterBrief?.inSum?.content}
        source={caseStudy.characterBrief?.inSum?.source || 'none'}
        onChange={(value) => handleFieldChange('characterBriefInSum', value)}
        readOnly={readOnly}
      />

      <CaseStudyField
        label="Real Issue"
        description="What's the real issue or conflict in this conversation"
        content={caseStudy.realIssue?.summary?.content}
        source={caseStudy.realIssue?.summary?.source || 'none'}
        onChange={(value) => handleFieldChange('realIssueSummary', value)}
        readOnly={readOnly}
      />

      <CaseStudyField
        label="Data & Reasoning"
        description="What leads you to conclude this issue needs addressing"
        content={caseStudy.realIssue?.dataAndReasoning?.content}
        source={caseStudy.realIssue?.dataAndReasoning?.source || 'none'}
        onChange={(value) => handleFieldChange('realIssueDataAndReasoning', value)}
        readOnly={readOnly}
      />

      {/* STAKES section */}
      <div className="cs-section-header">
        <div className="cs-field-header">
          <div className="cs-field-label">STAKES</div>
          <div className="cs-field-description">What&apos;s at stake for your counterpart</div>
        </div>
      </div>

      <div className="cs-stakes-row">
        <CaseStudyField
          label="Downsides"
          description="What they risk if this conversation goes badly"
          content={caseStudy.characterStakes?.downSides?.inSum?.content}
          source={caseStudy.characterStakes?.downSides?.inSum?.source || 'none'}
          onChange={(value) => handleFieldChange('characterStakesDownSidesInSum', value)}
          readOnly={readOnly}
        />

        <CaseStudyField
          label="Upsides"
          description="What they gain if this conversation goes well"
          content={caseStudy.characterStakes?.upSides?.inSum?.content}
          source={caseStudy.characterStakes?.upSides?.inSum?.source || 'none'}
          onChange={(value) => handleFieldChange('characterStakesUpSidesInSum', value)}
          readOnly={readOnly}
        />
      </div>


      <CaseStudyField
        label="Desired Outcome"
        description="What outcome you were hoping to achieve in this conversation"
        content={caseStudy.desiredOutcome?.content}
        source={caseStudy.desiredOutcome?.source || 'none'}
        onChange={(value) => handleFieldChange('desiredOutcome', value)}
        readOnly={readOnly}
      />

     <CaseStudyField
        label="Challenge"
        description="What, if anything, in the organization's structure, culture, leadership, or processes made this conversation hard to have?"
        content={caseStudy.challenge?.content}
        source={caseStudy.challenge?.source || 'none'}
        onChange={(value) => handleFieldChange('challenge', value)}
        readOnly={readOnly}
      />

      {showLearningGoal && (
        <CaseStudyField
          label="Learning Goal"
          description="What you want to develop about yourself as a communicator"
          content={learningGoal.content}
          source={learningGoal.source || 'none'}
          onChange={(value) => handleGoalsFieldChange('learningGoal', value)}
          readOnly={readOnly}
        />
      )}

    </div>
  );
});

CaseStudyForm.displayName = 'CaseStudyForm';

const leafShape = PropTypes.shape({ content: PropTypes.string, source: PropTypes.oneOf(['none', 'ai', 'user']) });

CaseStudyForm.propTypes = {
  caseStudy: PropTypes.shape({
    characterBrief: PropTypes.shape({ inSum: leafShape }),
    realIssue: PropTypes.shape({
      summary:          leafShape,
      dataAndReasoning: leafShape,
    }),
    characterStakes: PropTypes.shape({
      downSides: PropTypes.shape({ inSum: leafShape }),
      upSides:   PropTypes.shape({ inSum: leafShape }),
    }),
    challenge:      leafShape,
    desiredOutcome: leafShape,
  }).isRequired,
  userGoals: PropTypes.shape({
    learningGoal: leafShape,
  }),
  conversationId:     PropTypes.string.isRequired,
  onFieldChange:      PropTypes.func.isRequired,
  onGoalsFieldChange: PropTypes.func,
  onSaveStatusChange: PropTypes.func.isRequired,
  readOnly:           PropTypes.bool,
};

export default CaseStudyForm;